/**
 * The major features of Rankbist that have their own public page. One list,
 * used by the navbar, the Features page, the home page, the footer, the
 * sitemap and the "related features" links, so they always agree.
 *
 * Package availability is never written here: pages read it from the live
 * feature registry (`keys` → /api/features) and the plans on sale.
 * Adding an entry adds the page to the nav, sitemap and related links.
 */
import type { LucideIcon } from "lucide-react";
import {
  BarChart3, Clapperboard, FileCode2, MonitorCheck, Gauge, Globe2, Link2, LineChart, MapPin, PenLine, Plug, Radar, ScanSearch, Search, SearchCheck, Sparkles,
} from "lucide-react";

export type Tone = "violet" | "pink" | "sky" | "amber" | "emerald" | "indigo";

export interface FeaturePage {
  path: string;
  /** Short name, as in the navbar. */
  name: string;
  /** One line under the name in menus. */
  short: string;
  /** The customer problem it solves, in their words. */
  problem: string;
  /** What it does. */
  does: string;
  /** Why it matters — the practical result. */
  benefit: string;
  /** Feature-registry keys this page is about; the first one decides "included from". */
  keys: string[];
  icon: LucideIcon;
  tone: Tone;
  /** Primary = shown first and largest (nav, Features page, home). */
  tier: "primary" | "secondary";
  badge?: "New" | "Popular" | "Free";
}

export const FEATURE_PAGES: FeaturePage[] = [
  {
    path: "/creator-studio",
    name: "Creator Studio",
    short: "Shorts with captions and video SEO from real data.",
    problem: "Cutting short videos and writing titles, tags and chapters takes hours for every upload.",
    does: "Turns one long video into ready-to-post shorts with captions, and writes the title, description, chapters and tags from real searches.",
    benefit: "Post more often from the videos you already made, with metadata built on what people search for.",
    keys: ["creator.shorts", "creator.seo_package", "creator.captions", "creator.ideas", "creator.keywords"],
    icon: Clapperboard,
    tone: "emerald",
    tier: "primary",
    badge: "Popular",
  },
  {
    path: "/google-index-checker",
    name: "Google indexing",
    short: "Which pages Google indexed — and why the rest aren't.",
    problem: "A page that isn't in Google gets no search traffic, and Search Console makes it slow to find out which ones and why.",
    does: "Checks pages straight from Google's URL Inspection data, scores your index coverage, explains every “not indexed” reason in plain words and watches priority pages daily.",
    benefit: "Find and fix the pages Google skipped before they cost you traffic, and get told when one drops out.",
    keys: ["index.inspect", "index.coverage", "index.reasons", "index.monitor", "index.bulk", "index.indexnow"],
    icon: SearchCheck,
    tone: "sky",
    tier: "primary",
    badge: "New",
  },
  {
    path: "/page-speed-test",
    name: "Lighthouse & page speed",
    short: "Google Lighthouse beside every live-page check.",
    problem: "Slow pages and broken basics lose visitors and rankings, and running separate tools for content and speed wastes time.",
    does: "Runs Google Lighthouse (SEO and Performance) and real-visitor Core Web Vitals on the same click as Rankbist's content check, on mobile or desktop, and keeps every result.",
    benefit: "See what Google's own audit flags next to what Rankbist finds, fix the overlap first, and prove the score went up.",
    keys: ["site.speed", "check.url"],
    icon: Gauge,
    tone: "amber",
    tier: "primary",
    badge: "New",
  },
  {
    path: "/article-generator",
    name: "Article generator",
    short: "A brief in, a scored SEO article out.",
    problem: "AI drafts come out generic, short of the length you asked for and full of made-up numbers.",
    does: "Researches keywords, writes to a guaranteed word count, scores the draft with the same engine as every check and revises it, using placeholders instead of invented facts.",
    benefit: "Publish-ready drafts you can trust, with the claims you need to check clearly marked.",
    keys: ["gen.article", "gen.optimize", "gen.grounding", "gen.wordcount"],
    icon: PenLine,
    tone: "amber",
    tier: "primary",
  },
  {
    path: "/website-monitoring",
    name: "Website monitoring",
    short: "Every check on your site, every day, with an email report.",
    problem: "Problems on a live site show up weeks later as lost traffic, because nobody checks every day.",
    does: "Runs every check on your website at the end of each day (home page tests, Google speed, a full Site Audit and Search Console totals), emails what changed and what to fix next, and alerts you within the hour if the site goes down.",
    benefit: "Catch what broke yesterday instead of next month, with a short list of what to fix first.",
    keys: ["site.watch", "monitor.scheduled", "gsc.decay", "agent.fixes"],
    icon: MonitorCheck,
    tone: "violet",
    tier: "primary",
    badge: "New",
  },
  {
    path: "/website-analytics",
    name: "Website analytics",
    short: "Your traffic next to your rankings and scores.",
    problem: "Traffic lives in Google Analytics and rankings in Search Console, so nobody sees which page needs what.",
    does: "Shows visitors, channels, conversions and AI assistant visits from Google Analytics or a cookie-free script, every page next to its Google clicks, position, content score and speed, and what changed your traffic.",
    benefit: "Know which pages to fix first and whether your SEO work is bringing visitors and customers.",
    keys: ["analytics.overview", "analytics.pages", "analytics.conversions", "analytics.annotations", "analytics.tracker"],
    icon: BarChart3,
    tone: "sky",
    tier: "primary",
    badge: "New",
  },
  {
    path: "/site-audit",
    name: "Site audit",
    short: "Every page scanned, every problem listed with its fix.",
    problem: "Technical problems hide across hundreds of pages, and generic audits bury what matters under noise.",
    does: "Scans the whole site, gives a Health Score, lists every issue as an error, warning or notice with its fix, and re-audits on a schedule.",
    benefit: "Know exactly what to fix first, and see what got fixed or broke since last time.",
    keys: ["site.scan", "site.health", "site.schedule", "site.technical"],
    icon: Globe2,
    tone: "violet",
    tier: "primary",
  },
  {
    path: "/ai-search-visibility",
    name: "AI search visibility",
    short: "Brand Radar: whether AI answers name you.",
    problem: "More people ask AI assistants instead of searching, and you can't see whether they recommend you or a competitor.",
    does: "Brand Radar samples AI answers for your prompts every week, shows who gets named or cited, and pairs it with AI crawler access and AI referral traffic.",
    benefit: "Spot the questions where competitors are named instead of you, and track the trend as you improve.",
    keys: ["ai.brand_radar", "ai.citations", "site.ai_crawlers", "ga4.ai_traffic", "engine.ai_answer"],
    icon: Radar,
    tone: "pink",
    tier: "primary",
    badge: "New",
  },
  {
    path: "/rank-tracking",
    name: "Rank Tracker",
    short: "Daily Google positions from your own Search Console.",
    problem: "Guessed rankings from third-party tools rarely match what Google reports for your site.",
    does: "Tracks the keywords you choose with Google's own positions, stored daily and kept past Google's 16 months, with winners, losers and decay alerts.",
    benefit: "Real positions you can act on: pages close to page one, pages slipping, pages competing with each other.",
    keys: ["rank.tracked", "rank.history", "gsc.rankings", "gsc.opportunities", "gsc.decay"],
    icon: LineChart,
    tone: "emerald",
    tier: "secondary",
  },
  {
    path: "/keyword-research",
    name: "Keywords Explorer",
    short: "Ideas from Google, YouTube, Amazon and Bing.",
    problem: "Keyword tools invent volumes and miss what people actually type.",
    does: "Finds ideas and questions from real search suggestions on four platforms, with Bing's own numbers and your Search Console data for the keyword.",
    benefit: "Pick topics people search for, and see where your site already ranks for them.",
    keys: ["keywords.sources", "keywords.research", "keywords.bing_volume", "keywords.trending"],
    icon: Search,
    tone: "amber",
    tier: "secondary",
  },
  {
    path: "/competitor-research",
    name: "Backlinks & Site Explorer",
    short: "Authority Score, referring domains and link gaps.",
    problem: "You can't win links or rankings without knowing who links to competitors and not to you.",
    does: "Shows any domain's Authority Score, referring domains, new and lost links, and the link gap against up to three competitors.",
    benefit: "A list of sites worth reaching out to, and a clear view of who is gaining.",
    keys: ["explorer.overview", "backlinks.refdomains", "backlinks.changes", "backlinks.gap"],
    icon: Link2,
    tone: "pink",
    tier: "secondary",
  },
  {
    path: "/local-seo",
    name: "Local SEO",
    short: "Business details, Google Business Profile and directories.",
    problem: "Local customers can't find a business whose details disagree across its site, Google and directories.",
    does: "Checks your name, address, phone and hours against your site, writes a Google Business Profile content pack, lists the directories worth joining and turns owner-only jobs into guided tasks.",
    benefit: "Consistent details everywhere and a clear checklist for showing up in local searches.",
    keys: ["local.profile", "local.gbp", "local.citations", "local.tasks", "local.reviews"],
    icon: MapPin,
    tone: "emerald",
    tier: "secondary",
  },
  {
    path: "/ai-content-checks",
    name: "Content & AI-writing checks",
    short: "28 tests, plus the patterns that make AI text read generic.",
    problem: "Drafts that read as generic AI writing, with unsourced claims, don't earn trust or rankings.",
    does: "Scores content on 28 tests across 8 categories and flags unsourced claims, flat rhythm and missing first-hand experience, with the exact fix for each.",
    benefit: "Catch weak spots before readers and search engines do.",
    keys: ["engine.core", "engine.ai_quality", "report.detail", "fix.ai"],
    icon: Sparkles,
    tone: "violet",
    tier: "secondary",
  },
  {
    path: "/seo-files",
    name: "Sitemap, robots.txt & schema",
    short: "Files for your whole site, generated from a scan.",
    problem: "Writing a correct sitemap, robots.txt and structured data by hand is slow and easy to get wrong.",
    does: "Generates a sitemap, a robots.txt and JSON-LD for the whole site from a scan, with page-by-page guidance and step-by-step guides for your platform.",
    benefit: "Correct technical files in minutes, and a check that they are live.",
    keys: ["site.files", "site.guidance", "site.dev_guides"],
    icon: FileCode2,
    tone: "indigo",
    tier: "secondary",
  },
  {
    path: "/free-seo-checker",
    name: "Free SEO checker",
    short: "Score any content in seconds. No sign-up.",
    problem: "You want to know where a page stands before paying for anything.",
    does: "Scores pasted content with the real engine and shows the real number of issues.",
    benefit: "An honest first look, free.",
    keys: ["growth.public_checker"],
    icon: ScanSearch,
    tone: "sky",
    tier: "secondary",
    badge: "Free",
  },
  {
    path: "/integrations",
    name: "Integrations",
    short: "Search Console, Bing, GA4, WordPress and more.",
    problem: "Your data lives in several Google and Bing tools.",
    does: "Connects Search Console, Bing Webmaster Tools and Google Analytics 4, plus WordPress, Google Docs and Chrome.",
    benefit: "Your own numbers inside every report.",
    keys: ["gsc.rankings", "bing.data", "ga4.ai_traffic"],
    icon: Plug,
    tone: "indigo",
    tier: "secondary",
  },
];

export function featurePage(path: string): FeaturePage | undefined {
  return FEATURE_PAGES.find((p) => p.path === path);
}

/** Up to `n` other feature pages to link from one, preferring the ones listed in `prefer`. */
export function relatedPages(path: string, prefer: string[] = [], n = 4): FeaturePage[] {
  const others = FEATURE_PAGES.filter((p) => p.path !== path && p.path !== "/integrations");
  const first = prefer.map((x) => others.find((p) => p.path === x)).filter((p): p is FeaturePage => Boolean(p));
  const rest = others.filter((p) => !first.includes(p) && p.tier === "primary");
  return [...first, ...rest].slice(0, n);
}
